import React, { useEffect, useRef, useState } from 'react';
import './Info.css';
import './Subscribe.css';

// Using Kit's official embed: their JS (ck.5.js) handles the submission,
// double opt-in, spam checks etc. We override their inline styles with CSS
// to match the Thingdoms aesthetic.
const KIT_SCRIPT_SRC = 'https://f.convertkit.com/ckjs/ck.5.js';
const KIT_FORM_UID = '04e642d96e';
const KIT_FORM_ID = '9337554';

// Simple email shape check. Matches most real emails; avoids the heavy
// RFC-compliant regex (which would reject nothing interesting for our case).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Subscribe() {
  const formContainerRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Kit's script is a one-time global init. Only inject it once.
    if (!document.querySelector(`script[src="${KIT_SCRIPT_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = KIT_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    // Delegate hover on the submit button. Kit's JS may replace the button
    // after mount, so we attach via delegation on the form wrapper.
    const wrapper = formContainerRef.current;
    if (!wrapper) return;
    const handleOver = (e) => {
      const btn = e.target.closest('.formkit-submit');
      if (btn) setHovering(true);
    };
    const handleOut = (e) => {
      const btn = e.target.closest('.formkit-submit');
      if (btn) setHovering(false);
    };
    wrapper.addEventListener('mouseover', handleOver);
    wrapper.addEventListener('mouseout', handleOut);

    // Intercept submit in the capture phase, BEFORE Kit's bubble-phase handler.
    // Validate the email ourselves; if invalid, stop the event reaching Kit and
    // show our own styled error message.
    const form = wrapper.querySelector('form');
    const handleSubmitCapture = (e) => {
      const input = form.querySelector('input[name="email_address"]');
      const value = (input?.value || '').trim();
      if (!EMAIL_RE.test(value)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        setError('Please enter a valid email address.');
      } else {
        setError(null);
      }
    };
    // Clear error as soon as the user edits the field.
    const handleInput = () => setError(null);
    const input = form?.querySelector('input[name="email_address"]');
    form?.addEventListener('submit', handleSubmitCapture, true);
    input?.addEventListener('input', handleInput);

    // Kit's JS injects a SparkLoop / Creator Network "Thanks for subscribing"
    // modal into <body> after a successful submission. We don't want it —
    // watch for it, remove it immediately, and show our own success message.
    const bodyObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const cls = (node.className || '').toString();
          if (
            cls.includes('seva-overlay') ||
            cls.includes('formkit-overlay') ||
            cls.includes('seva-modal') ||
            cls.includes('formkit-modal')
          ) {
            node.remove();
            setSubscribed(true);
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });

    return () => {
      wrapper.removeEventListener('mouseover', handleOver);
      wrapper.removeEventListener('mouseout', handleOut);
      form?.removeEventListener('submit', handleSubmitCapture, true);
      input?.removeEventListener('input', handleInput);
      bodyObserver.disconnect();
    };
  }, []);

  // Apply the hover class imperatively so it survives Kit's DOM mutations.
  useEffect(() => {
    const btn = formContainerRef.current?.querySelector('.formkit-submit');
    if (!btn) return;
    if (hovering) btn.classList.add('subscribe-rainbow');
    else btn.classList.remove('subscribe-rainbow');
  }, [hovering]);

  return (
    <div>
      <div className="home-container small-width">
        <div className="container">
          <div className="body-text">
            {subscribed
              ? 'Check your inbox for a confirmation link. If you don’t see it, check your spam folder.'
              : 'Join the Thingdoms mailing list for project updates.'}
          </div>

          <div ref={formContainerRef} className="kit-form-wrapper" style={subscribed ? { display: 'none' } : undefined}>
            <form
              noValidate
              action={`https://app.kit.com/forms/${KIT_FORM_ID}/subscriptions`}
              className="seva-form formkit-form"
              method="post"
              data-sv-form={KIT_FORM_ID}
              data-uid={KIT_FORM_UID}
              data-format="inline"
              data-version="5"
              data-options='{"settings":{"after_subscribe":{"action":"message","success_message":"Check your inbox to confirm.","redirect_url":""},"analytics":{"google":null,"fathom":null,"facebook":null,"segment":null,"pinterest":null,"sparkloop":null,"googletagmanager":null},"modal":{"trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15},"powered_by":{"show":false,"url":""},"recaptcha":{"enabled":false},"return_visitor":{"action":"show","custom_content":""},"slide_in":{"display_in":"bottom_right","trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15},"sticky_bar":{"display_in":"top","trigger":"timer","scroll_percentage":null,"timer":5,"devices":"all","show_once_every":15}},"version":"5"}'
            >
              <div data-style="clean">
                <ul className="formkit-alert formkit-alert-error" data-element="errors" data-group="alert"></ul>
                <div data-element="fields" data-stacked="false" className="seva-fields formkit-fields">
                  <div className="formkit-field">
                    <input
                      className="formkit-input"
                      name="email_address"
                      aria-label="Email Address"
                      placeholder="email"
                      required
                      type="email"
                    />
                  </div>
                  <button data-element="submit" className="formkit-submit formkit-submit">
                    <div className="formkit-spinner"><div></div><div></div><div></div></div>
                    <span className="subscribe-text">
                      {'Subscribe'.split('').map((letter, index) => (
                        <span key={index} className="subscribe-letter" style={{ '--letter-index': index }}>
                          {letter}
                        </span>
                      ))}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>

          {error && !subscribed && (
            <div className="subscribe-error">{error}</div>
          )}

        </div>
      </div>
    </div>
  );
}
